/**
 * TurnIndicator — bandeau "À qui de jouer" affiché au-dessus du Board sur les
 * drills jouables (Opening Lab, Endgame Forge).
 *
 * Spec & contexte
 * ---------------
 * Léon (11 ans) a rapporté le 2026-05-17 : *« Je ne sais jamais si c'est à moi
 * de jouer. »* — pendant un drill, l'alternance Léon → adversaire scripté →
 * Léon n'a aucun signal visuel hors plateau. Inspiration : turn indicator
 * chess.com (clock highlight) + lichess (turn badge dans player bar).
 *
 * Trois `state` exclusifs :
 *  - `user-turn`           → texte volt + dot pulsant (signal "go").
 *  - `opponent-thinking`   → texte secondaire + dot 3-points animés.
 *  - `idle`                → texte gris discret, pas d'animation.
 *
 * Contraintes (DESIGN.md)
 * -----------------------
 *  - Tokens couleur uniquement (`--volt-500`, `--ink-300`) — aucune valeur hex.
 *  - Pas d'emoji, pas d'icône inventée. Si besoin → Phosphor (`@phosphor-icons/react`).
 *  - Tutoiement neutre ("À toi de jouer", pas "À vous").
 *  - Pas de "Bonjour Léon" ni signature (DESIGN §6).
 *  - Animation ≤ 400ms par défaut ; pulsation respectant
 *    `prefers-reduced-motion` (désactivée globalement via `globals.css`).
 *  - Pas de default export (CONVENTIONS §6).
 *
 * Placement
 * ---------
 * Le composant se place À CÔTÉ du Board, jamais dedans. Évite d'agréger une
 * responsabilité d'orchestration dans `app/src/chess/board.tsx` (le board
 * reste un primitive Chessground stateless).
 *
 * Délai d'affichage minimum côté caller
 * --------------------------------------
 * Quand l'adversaire est scripté (Endgame Forge : coup déjà connu, rejoué
 * via requestAnimationFrame), la transition `opponent-thinking → user-turn`
 * peut être instantanée et invisible. Le hook utilitaire `useMinDuration`
 * (cf. `./useMinDuration.ts`) permet au caller d'imposer un minimum de
 * 400ms d'affichage pour éviter le flash imperceptible. Voir usage dans
 * `EndgameDrillPage`.
 */

export type TurnIndicatorState = 'user-turn' | 'opponent-thinking' | 'idle';

interface TurnIndicatorProps {
  state: TurnIndicatorState;
  /** Texte affiché en `user-turn`. Défaut : « À toi de jouer ». */
  userLabel?: string;
  /** Texte affiché en `opponent-thinking`. Défaut : « L'adversaire réfléchit… ». */
  opponentLabel?: string;
  /** Texte affiché en `idle`. Défaut : « En attente ». */
  idleLabel?: string;
  className?: string;
}

const DEFAULT_USER_LABEL = 'À toi de jouer';
const DEFAULT_OPPONENT_LABEL = 'L’adversaire réfléchit…';
const DEFAULT_IDLE_LABEL = 'En attente';

/**
 * TurnIndicator — bandeau ~32-40px à placer juste au-dessus du Board.
 */
export function TurnIndicator({
  state,
  userLabel = DEFAULT_USER_LABEL,
  opponentLabel = DEFAULT_OPPONENT_LABEL,
  idleLabel = DEFAULT_IDLE_LABEL,
  className = '',
}: TurnIndicatorProps): JSX.Element {
  const label =
    state === 'user-turn' ? userLabel : state === 'opponent-thinking' ? opponentLabel : idleLabel;

  const textColor =
    state === 'user-turn'
      ? 'text-volt-500'
      : state === 'opponent-thinking'
        ? 'text-ink-300'
        : 'text-ink-300';

  return (
    <div
      data-testid="turn-indicator"
      data-state={state}
      role="status"
      aria-live="polite"
      aria-label={label}
      className={`flex items-center gap-2.5 min-h-[32px] py-1 text-body-sm font-medium ${textColor} ${className}`}
    >
      {state === 'user-turn' && <UserTurnDot />}
      {state === 'opponent-thinking' && <OpponentDots />}
      {state === 'idle' && <IdleDot />}
      <span>{label}</span>
    </div>
  );
}

// ─── Sous-composants visuels ───────────────────────────────────────────────

/**
 * Dot pulsant volt — signal "go" pour l'utilisateur.
 * Animation Tailwind `animate-pulse-soft` (~2.4s, défini dans
 * tailwind.config.ts). `prefers-reduced-motion` est respecté globalement
 * via `globals.css` (durée → 0.01ms, iteration-count → 1).
 */
function UserTurnDot(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="relative inline-flex items-center justify-center"
      style={{ width: 10, height: 10 }}
    >
      <span
        className="absolute inset-0 rounded-full animate-pulse-ring"
        style={{ backgroundColor: 'var(--volt-500)' }}
      />
      <span
        className="relative rounded-full"
        style={{ width: 10, height: 10, backgroundColor: 'var(--volt-500)' }}
      />
    </span>
  );
}

/**
 * Trois points qui clignotent en séquence (« … » classique de chargement).
 * Chaque point a un `animation-delay` décalé pour produire l'effet vague.
 * Couleur : `--ink-300` (paper secondaire, discret).
 */
function OpponentDots(): JSX.Element {
  // Délais voulus < 400ms par dot (DESIGN.md §9) — ici 0/180/360ms en
  // utilisant `animate-pulse-soft` (2.4s loop) avec offsets négatifs pour
  // démarrer à différentes phases de l'animation.
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center gap-1"
      style={{ height: 10 }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="block rounded-full animate-pulse-soft"
          style={{
            width: 4,
            height: 4,
            backgroundColor: 'var(--ink-300)',
            animationDelay: `${i * 180}ms`,
          }}
        />
      ))}
    </span>
  );
}

/**
 * Dot statique gris discret — état `idle` (partie finie, drill non démarré).
 */
function IdleDot(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="inline-block rounded-full"
      style={{ width: 6, height: 6, backgroundColor: 'var(--ink-300)', opacity: 0.5 }}
    />
  );
}

// Hook `useMinDuration` extrait dans `./useMinDuration.ts` pour ne pas casser
// le fast-refresh React (un fichier composant ne doit exporter que des
// composants — `react-refresh/only-export-components`).
